import { useEffect, useState } from "react";
import { Check, CreditCard, PartyPopper, Pause, Play, Clock, AlertTriangle, Sun, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_META = {
  trial:          { label: "Free trial",    tone: "bg-amber-50 text-amber-800 border-amber-200" },
  trial_expired:  { label: "Trial ended",   tone: "bg-red-50 text-red-800 border-red-200" },
  active:         { label: "Active",        tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  trialing:       { label: "Trialing",      tone: "bg-blue-50 text-blue-800 border-blue-200" },
  paused:         { label: "Paused",        tone: "bg-stone-100 text-stone-700 border-stone-300" },
  pause_expired:  { label: "At risk",       tone: "bg-red-50 text-red-800 border-red-300" },
  canceled:       { label: "Canceled",      tone: "bg-stone-100 text-stone-600 border-stone-300" },
};

export default function Billing() {
  const [tiers, setTiers] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [busyTier, setBusyTier] = useState(null);
  const [previews, setPreviews] = useState({}); // tierId -> { final_price, discount_pct, label }
  const [summary, setSummary] = useState({ students: [], pause_max_days: 100 });
  const [pauseBusy, setPauseBusy] = useState(null); // student.id currently pausing/resuming
  const [summerOpen, setSummerOpen] = useState(false);
  const [summerBusy, setSummerBusy] = useState(false);
  const [summerResumeDate, setSummerResumeDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return d.toISOString().slice(0, 10);
  });

  const refreshStudents = async () => {
    const s = await api.get("/students");
    setStudents(s.data);
    return s.data;
  };

  const refreshSummary = async () => {
    try {
      const { data } = await api.get("/subscriptions/subscription-summary");
      setSummary(data);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      const t = await api.get("/subscriptions/tiers");
      setTiers(t.data);
      const s = await refreshStudents();
      if (s.length) setStudentId(s[0].id);
      await refreshSummary();
    })().catch(() => {});
  }, []);

  useEffect(() => {
    if (!studentId || tiers.length === 0) return;
    (async () => {
      const results = {};
      for (const t of tiers) {
        try {
          const { data } = await api.get(`/subscriptions/pricing-preview`, {
            params: { student_id: studentId, tier: t.id },
          });
          results[t.id] = data;
        } catch (_e) { /* skip failed preview */ }
      }
      setPreviews(results);
    })();
  }, [studentId, tiers]);

  const startCheckout = async (tierId) => {
    if (!studentId) {
      toast.error("Add a student first");
      return;
    }
    setBusyTier(tierId);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        tier: tierId,
        student_id: studentId,
        origin_url: window.location.origin,
      });
      if (data.free) {
        toast.success(`Activated free — ${data.message}`);
        await refreshStudents();
        await refreshSummary();
        setBusyTier(null);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusyTier(null);
    }
  };

  const startSummerBreak = async () => {
    setSummerBusy(true);
    try {
      const { data } = await api.post("/subscriptions/summer-break", { resume_date: summerResumeDate });
      toast.success(`Summer break scheduled — ${data.paused_count} student(s) paused. Auto-resumes ${summerResumeDate}.`);
      setSummerOpen(false);
      await refreshStudents();
      await refreshSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSummerBusy(false);
    }
  };

  const pauseStudent = async (row) => {
    if (!window.confirm(`Pause ${row.name}'s subscription? You'll have up to ${summary.pause_max_days} days to resume without losing their profile.`)) return;
    setPauseBusy(row.id);
    try {
      await api.post(`/subscriptions/pause/${row.id}`, { reason: "summer break" });
      toast.success(`Paused ${row.name} — reactivate within ${summary.pause_max_days} days.`);
      await refreshStudents();
      await refreshSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPauseBusy(null);
    }
  };

  const resumeStudent = async (row) => {
    setPauseBusy(row.id);
    try {
      await api.post(`/subscriptions/resume/${row.id}`);
      toast.success(`${row.name} is back — welcome to a new term.`);
      await refreshStudents();
      await refreshSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPauseBusy(null);
    }
  };

  const student = students.find((s) => s.id === studentId);
  const anyDiscount = Object.values(previews).some((p) => p.discount_pct > 0);

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Billing</div>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Subscriptions</h1>
        <p className="mt-2 text-stone-500">Per-student pricing. Every additional student is 50% off, forever.</p>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
        <Label>Choose student</Label>
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger className="mt-2 rounded-xl h-11 max-w-md" data-testid="billing-student-select">
            <SelectValue placeholder="Select a student" />
          </SelectTrigger>
          <SelectContent>
            {students.length === 0 && <SelectItem value="__none__" disabled>Add a student first</SelectItem>}
            {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — G{s.grade}</SelectItem>)}
          </SelectContent>
        </Select>
        {student && (
          <div className="mt-3 text-sm text-stone-500">
            Current: <span className="font-medium text-stone-900 uppercase">{student.subscription_tier || "basic"}</span> · <span className={student.subscription_status === "active" ? "text-emerald-600" : "text-stone-500"}>{student.subscription_status || "trial"}</span>
          </div>
        )}
        {anyDiscount && (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="bulk-discount-banner">
            <PartyPopper className="w-4 h-4" />
            Bulk discount active for this student — pricing below reflects your savings.
          </div>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-display text-lg font-semibold">Subscription status per student</h3>
            <p className="text-sm text-stone-500 mt-1">7-day free trial per new student. Pause up to <strong>{summary.pause_max_days} days</strong> (summer break) without losing lessons, portfolio, or verses.</p>
          </div>
          <Button
            onClick={() => setSummerOpen(true)}
            className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white shadow-sm"
            data-testid="summer-break-btn"
          >
            <Sun className="w-4 h-4 mr-2" /> Summer break
          </Button>
        </div>
        {summary.students.length === 0 ? (
          <div className="mt-4 text-sm text-stone-500">Add a student to start tracking their subscription.</div>
        ) : (
          <div className="mt-4 divide-y divide-stone-100" data-testid="subscription-summary-list">
            {summary.students.map((row) => {
              const meta = STATUS_META[row.subscription_status] || STATUS_META.trial;
              const isPaused = row.subscription_status === "paused";
              const isPauseExpired = row.subscription_status === "pause_expired";
              const isTrialExpired = row.subscription_status === "trial_expired";
              const canPause = ["active", "trialing", "trial"].includes(row.subscription_status);
              const canResume = isPaused || isPauseExpired;
              return (
                <div key={row.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`sub-row-${row.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">
                      {row.name} <span className="text-stone-400 text-sm">— G{row.grade}</span>
                    </div>
                    <div className="mt-1 flex items-center flex-wrap gap-2 text-xs">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${meta.tone}`} data-testid={`sub-status-${row.id}`}>
                        {meta.label}
                      </span>
                      <span className="text-stone-400 uppercase tracking-[0.15em] font-semibold">
                        {row.subscription_tier}
                      </span>
                      {typeof row.trial_days_remaining === "number" && (
                        <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`trial-countdown-${row.id}`}>
                          <Clock className="w-3 h-3" />
                          Trial ends in {row.trial_days_remaining} day{row.trial_days_remaining === 1 ? "" : "s"}
                        </span>
                      )}
                      {isPaused && typeof row.pause_days_remaining === "number" && (
                        <span className={`inline-flex items-center gap-1 ${row.pause_days_remaining <= 20 ? "text-red-700 font-semibold" : "text-stone-600"}`} data-testid={`pause-countdown-${row.id}`}>
                          <Pause className="w-3 h-3" />
                          {row.pause_days_remaining} day{row.pause_days_remaining === 1 ? "" : "s"} left of pause
                          {row.pause_days_remaining <= 20 && <AlertTriangle className="w-3 h-3" />}
                        </span>
                      )}
                      {isPauseExpired && (
                        <span className="inline-flex items-center gap-1 text-red-700 font-semibold" data-testid={`pause-atrisk-${row.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Profile at risk — resume to reactivate
                        </span>
                      )}
                      {isTrialExpired && (
                        <span className="inline-flex items-center gap-1 text-red-700 font-semibold" data-testid={`trial-expired-${row.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Trial ended — choose a plan below
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canPause && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full border-stone-300"
                        disabled={pauseBusy === row.id}
                        onClick={() => pauseStudent(row)}
                        data-testid={`pause-btn-${row.id}`}
                        title="Pause for up to 100 days (summer break)"
                      >
                        <Pause className="w-3.5 h-3.5 mr-1" /> Pause
                      </Button>
                    )}
                    {canResume && (
                      <Button
                        size="sm"
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={pauseBusy === row.id}
                        onClick={() => resumeStudent(row)}
                        data-testid={`resume-btn-${row.id}`}
                      >
                        <Play className="w-3.5 h-3.5 mr-1" /> Resume
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {tiers.map((t) => {
          const p = previews[t.id];
          const finalPrice = p ? p.final_price : t.price;
          const isFree = p && p.discount_pct >= 100;
          const isDiscounted = p && p.discount_pct > 0;
          return (
            <div key={t.id} className={`relative rounded-3xl border p-8 bg-white ${t.id === "plus" ? "border-amber-400 shadow-[0_20px_60px_rgba(245,158,11,0.15)]" : "border-stone-200 shadow-sm"}`} data-testid={`billing-tier-${t.id}`}>
              {t.id === "plus" && !isDiscounted && (
                <div className="absolute -top-3 left-8 rounded-full bg-amber-500 text-white text-xs uppercase tracking-[0.15em] font-semibold px-3 py-1">Recommended</div>
              )}
              {isDiscounted && (
                <div className={`absolute -top-3 left-8 rounded-full ${isFree ? "bg-emerald-600" : "bg-emerald-500"} text-white text-xs uppercase tracking-[0.15em] font-semibold px-3 py-1`}>
                  {isFree ? "Free · bulk" : `${p.discount_pct}% off · bulk`}
                </div>
              )}
              <div className="font-display text-2xl font-semibold">{t.name}</div>
              <div className="text-stone-500 text-sm mt-1">{t.tagline}</div>
              <div className="mt-6 flex items-baseline gap-2">
                {isDiscounted && !isFree && (
                  <span className="font-display text-2xl font-medium text-stone-400 line-through">${t.price.toFixed(2)}</span>
                )}
                <span className="font-display text-5xl font-bold">${isFree ? "0" : finalPrice.toFixed(2)}</span>
                <span className="text-stone-500">/mo</span>
              </div>
              {p && p.label && (
                <div className="mt-2 text-xs text-emerald-700 font-medium" data-testid={`bulk-label-${t.id}`}>{p.label}</div>
              )}
              <ul className="mt-6 space-y-2 text-sm text-stone-700">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => startCheckout(t.id)}
                disabled={!studentId || busyTier === t.id}
                className={`mt-8 w-full rounded-full py-6 ${t.id === "plus" ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-stone-900 hover:bg-stone-800 text-white"}`}
                data-testid={`billing-checkout-${t.id}`}
              >
                {busyTier === t.id ? "Working…" : isFree ? "Activate free" : <><CreditCard className="w-4 h-4 mr-2" /> Subscribe</>}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
        <h3 className="font-display text-lg font-semibold">How bulk pricing works</h3>
        <ul className="mt-3 space-y-1 text-sm text-stone-700">
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 1st student on any paid tier: full price.</li>
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Every additional student: <strong>50% off</strong> the tier price, forever.</li>
        </ul>
      </div>

      <div className="text-xs text-stone-500">
        Test mode — use Stripe test card <code className="bg-stone-100 px-1.5 py-0.5 rounded">4242 4242 4242 4242</code>, any future date, any CVC.
      </div>

      {/* Summer Break planner dialog */}
      <Dialog open={summerOpen} onOpenChange={setSummerOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="summer-break-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-500" /> Summer break planner
            </DialogTitle>
            <DialogDescription>
              Pause every student profile until your chosen restart date. All portfolios, lessons, verses, and proficiency scores stay preserved. We&apos;ll automatically resume everyone on that date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Auto-resume date</Label>
              <Input
                type="date"
                value={summerResumeDate}
                onChange={(e) => setSummerResumeDate(e.target.value)}
                className="mt-2 rounded-xl h-11"
                min={new Date(Date.now() + 24*3600*1000).toISOString().slice(0,10)}
                data-testid="summer-resume-date"
              />
              <p className="mt-2 text-xs text-stone-500">Maximum {summary.pause_max_days} days out from today (Florida pause window).</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 leading-relaxed">
              <strong>Heads-up:</strong> All active/trial students will be paused. Anyone already paused stays paused.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSummerOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={startSummerBreak}
              disabled={summerBusy || !summerResumeDate}
              className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white"
              data-testid="summer-break-confirm"
            >
              {summerBusy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scheduling…</>) : (<><Sun className="w-4 h-4 mr-2" />Start summer break</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
